import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { userApi } from '../../../Utils/API';
import useLogout from '../../../hooks/useLogout';
import useOutsideClick from '../../../hooks/useOutsideClick';
import styles from './SupportTicket.module.scss';
import Widget from '../../../Components/Widget';
import CreateTicketModal from './LocalComponents/CreateTicketModal';
import ActionMenu from './LocalComponents/ActionMenu';
import EmptyState from './LocalComponents/EmptyState';
import SkeletonLoader from './LocalComponents/SkeletonLoader';
import TicketDetailsModal from './LocalComponents/TicketDetailsModal';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import Toast from '../../../Components/Toast';
import loadingGif from '../../../Assets/Animations/loading.gif';

const ITEMS_PER_LOAD = 10;

const SupportTicket = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [filteredData, setFilteredData] = useState([]);
    const [visibleItems, setVisibleItems] = useState(ITEMS_PER_LOAD);
    const [scrollLoading, setScrollLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdOn', direction: 'descending' });
    const [filters, setFilters] = useState({ category: '', status: '', source: 'dropdown' });
    const [tempFilters, setTempFilters] = useState({ category: '', status: '' });
    const [isFilterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketToClose, setTicketToClose] = useState(null);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [toast, setToast] = useState({ message: '', type: '' });
    const [stats, setStats] = useState({ Open: 0, Closed: 0, Pending: 0, Resolved: 0 });
    const [categories, setCategories] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const tableContainerRef = useRef(null);
    const filterPopoverRef = useRef(null);
    const loadingRef = useRef(false);
    const logout = useLogout();

    const fetchDropdownData = async () => {
        try {
            const token_type = localStorage.getItem('token_type');
            const access_token = localStorage.getItem('access_token');
            // if (!token_type || !access_token) {
            //     throw new Error('Authorization token not found.');
            // }
            const authHeader = { headers: { Authorization: `${token_type} ${access_token}` } };

            const [catResponse, statusResponse] = await Promise.all([
                userApi.get('/api/support-tickets/types', authHeader),
                userApi.get('/api/support-tickets/statuses', authHeader)
            ]);
            
            setCategories(catResponse.data);
            setStatuses(statusResponse.data);
        } catch (err) {
            console.error("Failed to fetch dropdown data.", err);
        }
    };

    const fetchTickets = async () => {
        setIsLoading(true);
        try {
            const token_type = localStorage.getItem('token_type');
            const access_token = localStorage.getItem('access_token');
            // if (!token_type || !access_token) {
            //     throw new Error('Authorization token not found.');
            // }

            const params = new URLSearchParams({
                page: 1,
                pageSize: 100000,
            });
            if (filters.status) params.append('status', filters.status);
            if (filters.category) params.append('ticketType', filters.category);

            const response = await userApi.get(`/api/support-tickets?${params.toString()}`, {
                headers: {
                    Authorization: `${token_type} ${access_token}`,
                },
            });

            const formattedTickets = response.data.tickets.map(ticket => ({
                ...ticket,
                createdOn: new Date(ticket.createdOn).toLocaleDateString('en-GB'),
            }));
            setTickets(formattedTickets);
            setStats(response.data.statusCounts);
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            } else {
                console.error("Failed to fetch support tickets.", err);
            }
        } finally {
            setIsLoading(false);
            setInitialLoad(false);
        }
    };
    
    useEffect(() => {
        if (filters.source === 'dropdown') {
            fetchTickets();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useOutsideClick([filterPopoverRef], () => {
        if (isFilterPopoverOpen) {
            setFilterPopoverOpen(false);
        }
    });

    useEffect(() => {
        if (location.state?.openModal) {
            setIsModalOpen(true);
        }
    }, [location.state]);

    useEffect(() => {
        setTempFilters(filters);
    }, [filters]);

    useEffect(() => {
        let data = [...tickets];

        if (filters.source === 'widget' && filters.status) {
            if (filters.status === 'Open') {
                const openStatuses = ['Open', 'ReOpen'];
                data = tickets.filter(item => openStatuses.includes(item.statusStr));
            } else if (filters.status === 'Pending Customer Response') {
                data = tickets.filter(item => item.statusStr === 'WaitingForResellerConfirmation');
            }
            else {
                data = tickets.filter(item => item.statusStr === filters.status);
            }
        } else if (filters.source === 'widget' && !filters.status) {
            data = [...tickets];
        }

        if (searchTerm) {
            data = data.filter(item =>
                item.ticketNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (sortConfig.key) {
            data.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
    
                if (sortConfig.key === 'createdOn') {
                    const dateA = new Date(aValue.split('/').reverse().join('-'));
                    const dateB = new Date(bValue.split('/').reverse().join('-'));
                    if (dateA < dateB) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                }
    
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
    
        setFilteredData(data);
        setVisibleItems(ITEMS_PER_LOAD);
        if (data.length === 0) {
            setIsLoading(false);
        }
    }, [tickets, searchTerm, sortConfig, filters]);

    const isFilterActive = filters.category !== '' || filters.status !== '';

    const handleScroll = () => {
        if (loadingRef.current || visibleItems >= filteredData.length) return;

        const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;

        if (scrollHeight - scrollTop <= clientHeight + 50) {
            loadingRef.current = true;
            setScrollLoading(true);
            setTimeout(() => {
                setVisibleItems((prev) => prev + ITEMS_PER_LOAD);
                setScrollLoading(false);
                loadingRef.current = false;
            }, 1000);
        }
    };

    useEffect(() => {
        const tableContainer = tableContainerRef.current;
        if (tableContainer) {
            tableContainer.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (tableContainer) {
                tableContainer.removeEventListener('scroll', handleScroll);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleItems, filteredData, isLoading]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return <i className="material-icons sort-material-icons">{sortConfig.direction === 'ascending' ? 'arrow_upward' : 'arrow_downward'}</i>;
        }
        return null;
    };

    const handleTempFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        setFilters({ ...tempFilters, source: 'dropdown' });
        setFilterPopoverOpen(false);
    };

    const handleClearFilters = () => {
        const clearedFilters = { category: '', status: '', source: 'dropdown' };
        setTempFilters(clearedFilters);
        setFilters(clearedFilters);
        setFilterPopoverOpen(false);
    };
    
    const handleWidgetClick = (status) => {
        setFilters({ category: '', status, source: 'widget' });
    };
    
    const handleCreateTicket = async (newTicket) => {
        try {
            const token_type = localStorage.getItem('token_type');
            const access_token = localStorage.getItem('access_token');
            // if (!token_type || !access_token) {
            //     throw new Error('Authorization token not found.');
            // }

            await userApi.post('/api/support-tickets', newTicket, {
                headers: {
                    Authorization: `${token_type} ${access_token}`,
                },
            });
            
            fetchTickets(); // Refetch tickets after successful creation
            setToast({ message: 'Ticket created successfully!', type: 'success' });
        } catch (err) {
            const errorMessage = err.response?.data || 'An unexpected error occurred.';
            console.error("Failed to create ticket.", err);
            setToast({ message: errorMessage, type: 'error' });
        }
    };

    const openCloseConfirmation = (ticket) => {
        setTicketToClose(ticket);
        setConfirmModalOpen(true);
    };

    const handleCloseTicket = async () => {
        if (!ticketToClose) return;

        try {
            const token_type = localStorage.getItem('token_type');
            const access_token = localStorage.getItem('access_token');
            // if (!token_type || !access_token) {
            //     throw new Error('Authorization token not found.');
            // }

            await userApi.post(`/api/support-tickets/${ticketToClose.id}/close`, {}, {
                headers: {
                    Authorization: `${token_type} ${access_token}`,
                },
            });
            
            fetchTickets(); // Refetch tickets after closing
            setToast({ message: 'Ticket closed successfully!', type: 'success' });
        } catch (err) {
            const errorMessage = err.response?.data || 'An unexpected error occurred.';
            console.error("Failed to close ticket.", err);
            setToast({ message: errorMessage, type: 'error' });
        } finally {
            setConfirmModalOpen(false);
            setTicketToClose(null);
        }
    };

    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
        setIsDetailsModalOpen(true);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'Resolved': return styles.statusResolved;
            case 'Closed': return styles.statusClosed;
            case 'In Progress': return styles.statusInProgress;
            case 'Pending Customer Response': return styles.statusPending;
            default: return styles.statusOpen;
        }
    };

    const uniqueCategories = useMemo(() => [{ text: 'All', value: '' }, ...categories], [categories]);
    const uniqueStatuses = useMemo(() => [{ text: 'All', value: '' }, ...statuses], [statuses]);

    if (isLoading) {
        return <SkeletonLoader />;
    }

    return (
        <div className={styles.supportTicketContainer}>
            <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleCloseTicket}
                title="Confirm Close Ticket"
                message={`Are you sure you want to close ticket #${ticketToClose?.ticketNo}?`}
            />
            <CreateTicketModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    if (location.state?.openModal) {
                        navigate(location.pathname, { replace: true, state: {} });
                    }
                }}
                onSave={handleCreateTicket}
                categories={categories}
            />
            <TicketDetailsModal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} ticket={selectedTicket} />
            <div className={styles.widgetsContainer}>
                <Widget title="Total Tickets" value={Object.values(stats).reduce((a, b) => a + b, 0)} icon="receipt_long" tooltip="Total number of tickets raised." onClick={() => handleWidgetClick('')} isActive={filters.status === ''} color="accent" />
                <Widget title="Open Tickets" value={stats.Open || 0} icon="hourglass_top" tooltip="Tickets that are currently open." onClick={() => handleWidgetClick('Open')} isActive={filters.status === 'Open'} color="accent" />
                <Widget title="Awaiting Response" value={stats.Pending || 0} icon="pending_actions" tooltip="Tickets pending customer response." onClick={() => handleWidgetClick('Pending Customer Response')} isActive={filters.status === 'Pending Customer Response'} color="info" />
                <Widget title="Resolved Tickets" value={stats.Resolved || 0} icon="task_alt" tooltip="Tickets that have been resolved." onClick={() => handleWidgetClick('Resolved')} isActive={filters.status === 'Resolved'} color="success" />
                <Widget title="Closed Tickets" value={stats.Closed || 0} icon="highlight_off" tooltip="Tickets that are closed." onClick={() => handleWidgetClick('Closed')} isActive={filters.status === 'Closed'} color="danger" />
            </div>
            <div className={styles.header}>
                <h3 className={styles.title}>Support Tickets ({filteredData.length})</h3>
                <div className={styles.tableActions}>
                    <div className={styles.mobileActionsContainer}>
                        <div className={styles.searchContainer}>
                            <i className={`material-icons ${styles.searchIcon}`}>search</i>
                            <input
                                type="text"
                                placeholder="Search by Ticket No or Title..."
                                className={styles.searchInput}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && <i className={`material-icons ${styles.closeIcon}`} onClick={() => setSearchTerm('')}>close</i>}
                        </div>
                        <div className={styles.filterContainer}>
                            <button className={`${styles.filterButton} ${isFilterActive ? styles.filterActive : ''}`} onClick={() => setFilterPopoverOpen(prev => !prev)}>
                                <i className="material-icons">filter_list</i>
                            </button>
                            {isFilterPopoverOpen && (
                                <div className={styles.filterPopover} ref={filterPopoverRef}>
                                    <div className={styles.filterSection}>
                                        <label>Category</label>
                                        <select name="category" value={tempFilters.category} onChange={handleTempFilterChange}>
                                            {uniqueCategories.map(cat => <option key={cat.value} value={cat.value}>{cat.text}</option>)}
                                        </select>
                                    </div>
                                    <div className={styles.filterSection}>
                                        <label>Status</label>
                                        <select name="status" value={tempFilters.status} onChange={handleTempFilterChange}>
                                            {uniqueStatuses.map(status => <option key={status.value} value={status.value}>{status.text}</option>)}
                                        </select>
                                    </div>
                                    <div className={styles.filterActions}>
                                        <button onClick={handleApplyFilters}>Apply</button>
                                        <button onClick={handleClearFilters}>Clear</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <button className={styles.createTicketButton} onClick={() => setIsModalOpen(true)}>Create Ticket</button>
                </div>
            </div>
            <div className={styles.tableContainer} ref={tableContainerRef}>
                <table className={styles.supportTicketTable}>
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('ticketNo')}>Ticket No {getSortIndicator('ticketNo')}</th>
                            <th onClick={() => requestSort('title')}>Title {getSortIndicator('title')}</th>
                            <th onClick={() => requestSort('typeStr')}>Category {getSortIndicator('typeStr')}</th>
                            <th onClick={() => requestSort('statusStr')} className={styles.statusColumn}>Status {getSortIndicator('statusStr')}</th>
                            <th onClick={() => requestSort('createdBy')}>Created By {getSortIndicator('createdBy')}</th>
                            <th onClick={() => requestSort('createdOn')}>Created On {getSortIndicator('createdOn')}</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading && filteredData.length > 0 ? (
                            filteredData.slice(0, visibleItems).map((ticket) => (
                                <tr key={ticket.ticketNo}>
                                    <td onClick={() => handleTicketClick(ticket)} className={styles.ticketNumber} data-label="Ticket No">{ticket.ticketNo}</td>
                                    <td data-label="Title">{ticket.title}</td>
                                    <td data-label="Category">{ticket.typeStr}</td>
                                    <td data-label="Status" className={styles.statusColumn}><span className={`${styles.status} ${getStatusClass(ticket.statusStr)}`}>{ticket.statusStr}</span></td>
                                    <td data-label="Created By">{ticket.createdBy}</td>
                                    <td data-label="Created On">{ticket.createdOn}</td>
                                    <td data-label="Actions" onClick={(e) => e.stopPropagation()}>
                                        <ActionMenu
                                            onViewDetails={() => handleTicketClick(ticket)}
                                            onCloseTicket={() => openCloseConfirmation(ticket)}
                                            isCloseable={['Open', 'Resolved'].includes(ticket.statusStr)}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : !initialLoad && !isLoading && (
                            <tr>
                                <td colSpan="7" className={styles.noDataCell}>
                                    <EmptyState onClearFilters={handleClearFilters} />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {scrollLoading && (
                    <div className={styles.loadingContainer}>
                        <img src={loadingGif} alt="Loading..." />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportTicket;